import { Circle, Polygon } from 'react-leaflet';
import * as L from 'leaflet';
import GeofenceSelectedTooltip from '../../tooltips/geofence-selected-tooltip/GeofenceSelectedTooltip';
import { ReactNode, useCallback } from 'react';
import { useSiteMapFocus } from '@casops/routes/home/useSiteMapFocus';
import BasicTooltip from '../../tooltips/basic-tooltip/BasicTooltip';
import { GEOFENCE_KEY, HOME_OVERVIEW_GEOFENCES, GEOFENCE_SUB_ROUTE } from '@casops/navigation';
import { commonPathDisplay, GeofenceLayerProps } from '../geofence-layer/GeofenceLayer';
import { useMatch } from 'react-router-dom';

export function GeofenceInteractiveLayer({
  feature: { geometry, properties },
  pathOptions = commonPathDisplay,
}: GeofenceLayerProps): ReactNode {
  const { value: geofenceId, focus: geofenceFocus } = useSiteMapFocus({
    paramKey: GEOFENCE_KEY,
    pathname: HOME_OVERVIEW_GEOFENCES,
  });

  const geofenceMatch = useMatch(GEOFENCE_SUB_ROUTE);
  const isGeofenceSubRoute = Boolean(geofenceMatch);

  const onClick = useCallback((): void => {
    geofenceFocus(properties.id);
  }, [geofenceFocus, properties.id]);

  const isSelected = typeof geofenceId === 'number' && properties.id === geofenceId;

  // A key must be used on the shape layer to re-render the whole layer when isSelected changes.
  // Without this, the tooltip does not accurately reflect the current state, and behaves inconsistently with mouse events.
  // Props such as 'permanent' do not function as expected if the props change after mounting.
  const renderKey = `${properties.id}--${isSelected ? 'selected' : 'not-selected'}`;

  const commonProps = {
    ...pathOptions,
    pmIgnore: true, // Disable geoman manipulation.
    interactive: true,
    eventHandlers: isGeofenceSubRoute ? undefined : { click: onClick },
  };

  if (geometry.type === 'Polygon') {
    const coords = L.GeoJSON.coordsToLatLngs(geometry.coordinates, 1, L.GeoJSON.coordsToLatLng);
    const positionKey = JSON.stringify(coords);

    return (
      <Polygon key={renderKey} {...commonProps} positions={coords}>
        {isSelected ? (
          <GeofenceSelectedTooltip
            key={positionKey}
            id={properties.id}
            name={properties.name}
            pdrpasset={properties.pdrpasset}
            pdrptime={properties.pdrptime}
            ra={properties.ra}
          />
        ) : (
          <BasicTooltip text={properties.name} />
        )}
      </Polygon>
    );
  } else if (
    geometry.type === 'Point' &&
    typeof properties.radius === 'number' &&
    geometry.coordinates.length >= 2
  ) {
    const centre = L.GeoJSON.coordsToLatLng([geometry.coordinates[0], geometry.coordinates[1]]);
    const positionKey = JSON.stringify(centre);

    return (
      <Circle key={renderKey} {...commonProps} center={centre} radius={properties.radius}>
        {isSelected ? (
          <GeofenceSelectedTooltip
            key={positionKey}
            id={properties.id}
            name={properties.name}
            pdrpasset={properties.pdrpasset}
            pdrptime={properties.pdrptime}
            ra={properties.ra}
          />
        ) : (
          <BasicTooltip text={properties.name} />
        )}
      </Circle>
    );
  }

  return null;
}

export default GeofenceInteractiveLayer;
